import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import HalaRoboAdvanced from './components/HalaRoboAdvanced';
import InventoryAutoMailScheduler from './components/InventoryAutoMailScheduler';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadialBarChart, RadialBar, ComposedChart, Treemap, Scatter, ScatterChart } from 'recharts';
import './App.css';
import ExpiryStoreDashboard from './components/ExpiryStoreDashboard';
import ActivityInfoSettings from './components/ActivityInfoSettings';
import UtilizationDashboard from './components/Utilizatationdashboard';
import OperationsConsole from './components/OperationsConsole';
import DynamicCapacityVerification from './components/DynamicCapacityVerification';
import ExecutiveDashboard from './components/ExecutiveDashboard';
import SalesDashboard from './components/SalesDashboard';
import OperationsDashboardV2 from './components/v2/OperationsDashboard';
import { ThemeProvider, ThemeToggle } from './ThemeContext';

import { getConfig } from './runtimeConfig';
import { smartFetch, checkServerVersion, clearCache } from './cacheManager';
const API = getConfig().REACT_APP_API;

// ============================================================

const CHART_COLORS = { primary: ['#6366f1', '#818cf8', '#a5b4fc'], secondary: ['#06b6d4', '#22d3ee', '#67e8f9'], success: ['#10b981', '#34d399', '#6ee7b7'], warning: ['#f59e0b', '#fbbf24', '#fcd34d'], accent: ['#8b5cf6', '#a78bfa', '#c4b5fd'], danger: ['#f43f5e', '#fb7185', '#fda4af'] };

const WAREHOUSE_NAMES: Record<string, string> = {
  'DPW': 'Dammam port WH', 'DSI': 'Dammam 2nd', 'JDA': 'Jeddah Modon3A', 'JDB': 'Jeddah 3B',
  'JUB': 'Jubail WH', 'JUB_1': 'JUBAIL 1 WH', 'JUB_2': 'JUBAIL 2', 'RUH': 'Hala Riyadh WH', 'RUH_M': 'HALA Riyadh WH'
};
const getWarehouseDisplayName = (code: string): string => WAREHOUSE_NAMES[code] || code;

interface Stats { TOTAL_BINS: number; TOTAL_WAREHOUSES: number; TOTAL_SHELF_SIZES: number; TOTAL_STORAGE_AREAS: number; TOTAL_SEGMENTS: number; FREE_SEGMENTS: number; USED_SEGMENTS: number; FREE_PCT: number; UTILIZED_PCT: number; AVG_LEVELS: number; MAX_LEVELS: number; MIN_LEVELS: number; }
interface VirtualStats { TOTAL_BINS: number; TOTAL_WAREHOUSES: number; TOTAL_SHELF_SIZES: number; TOTAL_STORAGE_AREAS: number; TOTAL_SEGMENTS: number; FREE_SEGMENTS: number; USED_SEGMENTS: number; FREE_PCT: number; UTILIZED_PCT: number; AVG_LEVELS: number; MAX_LEVELS: number; MIN_LEVELS: number; }
interface Filters { warehouses: (string | number)[]; shelfSizes: (string | number)[]; storageAreas: (string | number)[]; levels: (string | number)[]; abcClasses: (string | number)[]; storageZones: (string | number)[]; }
interface CustomerStorageStats { TOTAL_CUSTOMERS: number; PHYSICAL_BIN_CAPACITY: number; BIN_USED_PALLETS: number; VIRTUAL_USED_PALLETS: number; FLOOR_USED_PALLETS: number; TOTAL_USED_PALLETS: number; BIN_FREE_CAPACITY: number; BIN_UTILIZATION_PCT: number; FREE_PCT: number; TEMP_BLOCKED_PALLETS: number; TEMP_BLOCKED_BINS: number; MANUAL_CUSTOMERS: number; MANUAL_PALLETS: number; MANUAL_CAPACITY: number; TOTAL_CAPACITY_WITH_MANUAL: number; AREA_CUSTOMER_BLOCKED_BINS: number; AREA_CUSTOMER_BLOCKED_PALLETS: number; AREA_CUSTOMER_FULL_BLOCKED: number; }

function App() {
  // ===== STATE =====
  const [connected, setConnected] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showHalaRobo, setShowHalaRobo] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);
  const [warehouseData, setWarehouseData] = useState<any[]>([]);
  const [shelfData, setShelfData] = useState<any[]>([]);
  const [areaData, setAreaData] = useState<any[]>([]);
  const [rawWarehouseData, setRawWarehouseData] = useState<any[]>([]);
  const [virtualStats, setVirtualStats] = useState<VirtualStats | null>(null);
  const [virtualWarehouseData, setVirtualWarehouseData] = useState<any[]>([]);
  const [virtualAreaData, setVirtualAreaData] = useState<any[]>([]);
  const [rawVirtualData, setRawVirtualData] = useState<any[]>([]);
  const [filteredVirtualData, setFilteredVirtualData] = useState<any[]>([]);
  const [customerStorageData, setCustomerStorageData] = useState<any[]>([]);
  const [customerStorageStats, setCustomerStorageStats] = useState<CustomerStorageStats | null>(null);
  const [manualEntries, setManualEntries] = useState<any[]>([]);
  const [areaCustomerBlockedData, setAreaCustomerBlockedData] = useState<any[]>([]);
  const [areaCustomerBlockedStats, setAreaCustomerBlockedStats] = useState<any>(null);
  const [uomData, setUomData] = useState<{ summary: any; data: any[] }>({ summary: {}, data: [] });
  const [filters, setFilters] = useState<Filters>({ warehouses: [], shelfSizes: [], storageAreas: [], levels: [], abcClasses: [], storageZones: [] });
  
  const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>([]);
  const [selectedStorageAreas, setSelectedStorageAreas] = useState<string[]>([]);
  const [selectedShelfSizes, setSelectedShelfSizes] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [expandedFilter, setExpandedFilter] = useState<string | null>(null);
  const [activeMenu, setActiveMenu] = useState<'operations' | 'customer' | 'expire' | 'activity' | 'automail' | 'utilization' | 'capacity' | 'sales'>(() => {
    // Hash-based routing: #sales opens Sales Dashboard directly (shareable link)
    const hash = window.location.hash.replace('#', '');
    if (hash === 'sales') return 'sales';
    return 'operations';
  });
  
  const [expiryLoading, setExpiryLoading] = useState(false);
  const [expiryStorageData, setExpiryStorageData] = useState<any[]>([]);
  const [expiryStats, setExpiryStats] = useState<any>(null);
  const [storageAgeDistribution, setStorageAgeDistribution] = useState<any[]>([]);
  const [expiryDistribution, setExpiryDistribution] = useState<any[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [nextRefresh, setNextRefresh] = useState<number>(300);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const isApplyingFilters = useRef(false);
  const filterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dataLoadedRef = useRef(false);

  // AUTO LOAD DATA ON STARTUP
  useEffect(() => {
    testConnection();
  }, []);

  // Load data after connection is established
  useEffect(() => {
    if (connected && !dataLoadedRef.current) {
      dataLoadedRef.current = true;
      loadAllData();
    }
  }, [connected]);

  // ⚡ Smart auto-refresh: checks server version first, only re-fetches if data changed
  const lastDataHashRef = useRef<string>('');
  useEffect(() => { if (!autoRefreshEnabled || !stats) return; const timer = setInterval(() => { setNextRefresh(prev => { if (prev <= 1) {
    // Check server version before full re-fetch
    checkServerVersion(API).then(version => {
      if (version && version.hash !== lastDataHashRef.current) {
        console.log('⚡ Server data changed, refreshing...');
        lastDataHashRef.current = version.hash;
        loadAllData(true); // force refresh
      } else {
        console.log('⚡ Server data unchanged, skipping refresh');
      }
    }).catch(() => loadAllData(true));
    return 300; } return prev - 1; }); }, 1000); return () => clearInterval(timer); }, [autoRefreshEnabled, stats]);

  const availableStorageAreas = useMemo(() => {
    if (selectedWarehouses.length === 0 || !rawWarehouseData.length) return filters.storageAreas || [];
    return Array.from(new Set(rawWarehouseData.filter(item => selectedWarehouses.includes(item.WAREHOUSE)).map(item => item.STORAGE_AREA).filter(Boolean))).sort();
  }, [selectedWarehouses, rawWarehouseData, filters.storageAreas]);

  const availableShelfSizes = useMemo(() => {
    if (selectedWarehouses.length === 0 || !rawWarehouseData.length) return filters.shelfSizes || [];
    let filtered = rawWarehouseData.filter(item => selectedWarehouses.includes(item.WAREHOUSE));
    if (selectedStorageAreas.length > 0) filtered = filtered.filter(item => selectedStorageAreas.includes(item.STORAGE_AREA));
    return Array.from(new Set(filtered.map(item => item.SHELF_SIZE).filter(Boolean))).sort();
  }, [selectedWarehouses, selectedStorageAreas, rawWarehouseData, filters.shelfSizes]);

  const availableLevels = useMemo(() => {
    if (selectedWarehouses.length === 0 || !rawWarehouseData.length) return filters.levels || [];
    let filtered = rawWarehouseData.filter(item => selectedWarehouses.includes(item.WAREHOUSE));
    if (selectedStorageAreas.length > 0) filtered = filtered.filter(item => selectedStorageAreas.includes(item.STORAGE_AREA));
    if (selectedShelfSizes.length > 0) filtered = filtered.filter(item => selectedShelfSizes.includes(item.SHELF_SIZE));
    return Array.from(new Set(filtered.map(item => item.NUMBER_OF_LEVELS).filter(l => l != null))).sort((a, b) => Number(a) - Number(b));
  }, [selectedWarehouses, selectedStorageAreas, selectedShelfSizes, rawWarehouseData, filters.levels]);

  const getFilterStats = useMemo(() => {
    const calcStats = (data: any[]) => ({
      totalBinSpace: data.reduce((sum, item) => sum + (item.TOTAL_NO_OF_SEGMENTS || 0), 0),
      usedPallets: data.reduce((sum, item) => sum + ((item.TOTAL_NO_OF_SEGMENTS || 0) - (item.FREE_SEGMENTS || 0)), 0),
      freePalletSpace: data.reduce((sum, item) => sum + (item.FREE_SEGMENTS || 0), 0)
    });
    let baseData = rawWarehouseData;
    const warehouseStats = calcStats(baseData);
    let areaBaseData = baseData;
    if (selectedWarehouses.length > 0) areaBaseData = areaBaseData.filter(item => selectedWarehouses.includes(item.WAREHOUSE));
    const storageAreaStats = calcStats(areaBaseData);
    let shelfBaseData = areaBaseData;
    if (selectedStorageAreas.length > 0) shelfBaseData = shelfBaseData.filter(item => selectedStorageAreas.includes(item.STORAGE_AREA));
    const shelfSizeStats = calcStats(shelfBaseData);
    let levelsBaseData = shelfBaseData;
    if (selectedShelfSizes.length > 0) levelsBaseData = levelsBaseData.filter(item => selectedShelfSizes.includes(item.SHELF_SIZE));
    const levelsStats = calcStats(levelsBaseData);
    return { warehouseStats, storageAreaStats, shelfSizeStats, levelsStats };
  }, [rawWarehouseData, selectedWarehouses, selectedStorageAreas, selectedShelfSizes]);

  const currentFilteredStats = useMemo(() => {
    if (!stats) return null;
    return { totalBinSpace: stats.TOTAL_SEGMENTS, usedPallets: stats.USED_SEGMENTS, freePalletSpace: stats.FREE_SEGMENTS };
  }, [stats]);

  const generateAlerts = useMemo(() => {
    const alerts: any[] = [];
    if (stats) {
      if (stats.UTILIZED_PCT > 90) alerts.push({ type: 'critical', icon: '🚨', title: 'Critical Capacity Alert', message: `Physical storage at ${stats.UTILIZED_PCT}% capacity.`, time: 'Just now' });
      else if (stats.UTILIZED_PCT > 80) alerts.push({ type: 'warning', icon: '⚠️', title: 'High Utilization Warning', message: `Physical storage at ${stats.UTILIZED_PCT}% capacity.`, time: '5 mins ago' });
      if (stats.FREE_PCT > 40) alerts.push({ type: 'success', icon: '✅', title: 'Healthy Storage Levels', message: `${stats.FREE_PCT}% storage available.`, time: '10 mins ago' });
    }
    if (customerStorageStats && customerStorageStats.TOTAL_CUSTOMERS > 0) alerts.push({ type: 'info', icon: '👥', title: 'Customer Activity', message: `${customerStorageStats.TOTAL_CUSTOMERS} active customers with ${(customerStorageStats.TOTAL_USED_PALLETS || 0).toLocaleString()} pallets.`, time: '20 mins ago' });
    return alerts.slice(0, 4);
  }, [stats, customerStorageStats]);

  const testConnection = async () => { try { const res = await axios.get(`${API}/test`); setConnected(res.data.success); if (res.data.success) { loadFilters(); } } catch (err: any) { setConnected(false); setError(err.message); } };
  const loadFilters = async () => { try { const res = await axios.get(`${API}/warehouse/filters`); if (res.data.success) setFilters(res.data.data); } catch (err: any) { console.error('Failed to load filters:', err); } };

  const applyClientSideFilters = useCallback(() => {
    if (isApplyingFilters.current) return;
    isApplyingFilters.current = true;
    try {
      if (rawWarehouseData.length === 0) { setStats(null); setWarehouseData([]); setShelfData([]); setAreaData([]); return; }
      
      let filtered = [...rawWarehouseData];
      if (selectedWarehouses.length > 0) filtered = filtered.filter(item => selectedWarehouses.includes(item.WAREHOUSE));
      if (selectedStorageAreas.length > 0) filtered = filtered.filter(item => selectedStorageAreas.includes(item.STORAGE_AREA));
      if (selectedShelfSizes.length > 0) filtered = filtered.filter(item => selectedShelfSizes.includes(item.SHELF_SIZE));
      if (selectedLevels.length > 0) filtered = filtered.filter(item => selectedLevels.includes(String(item.NUMBER_OF_LEVELS)));
      
      let filteredVirtual = [...(rawVirtualData || [])];
      if (selectedWarehouses.length > 0) filteredVirtual = filteredVirtual.filter(item => selectedWarehouses.includes(item.WAREHOUSE));
      if (selectedStorageAreas.length > 0) filteredVirtual = filteredVirtual.filter(item => selectedStorageAreas.includes(item.STORAGE_AREA));
      setFilteredVirtualData(filteredVirtual);
      
      if (filtered.length > 0) {
        const totalBins = filtered.length;
        const totalSegments = filtered.reduce((sum, item) => sum + (item.TOTAL_NO_OF_SEGMENTS || 0), 0);
        const freeSegments = filtered.reduce((sum, item) => sum + (item.FREE_SEGMENTS || 0), 0);
        const usedSegments = totalSegments - freeSegments;
        const freePct = totalSegments > 0 ? Math.round((freeSegments / totalSegments) * 100 * 100) / 100 : 0;
        const utilizedPct = totalSegments > 0 ? Math.round((usedSegments / totalSegments) * 100 * 100) / 100 : 0;
        const warehouses = Array.from(new Set(filtered.map(item => item.WAREHOUSE)));
        const shelfSizes = Array.from(new Set(filtered.map(item => item.SHELF_SIZE)));
        const storageAreas = Array.from(new Set(filtered.map(item => item.STORAGE_AREA)));
        const levels = filtered.map(item => item.NUMBER_OF_LEVELS).filter(l => l != null);
        const avgLevels = levels.length > 0 ? levels.reduce((a, b) => a + b, 0) / levels.length : 0;
        const maxLevels = levels.length > 0 ? levels.reduce((a, b) => a > b ? a : b, levels[0]) : 0;
        const minLevels = levels.length > 0 ? levels.reduce((a, b) => a < b ? a : b, levels[0]) : 0;
        
        setStats({ TOTAL_BINS: totalBins, TOTAL_WAREHOUSES: warehouses.length, TOTAL_SHELF_SIZES: shelfSizes.length, TOTAL_STORAGE_AREAS: storageAreas.length, TOTAL_SEGMENTS: totalSegments, FREE_SEGMENTS: freeSegments, USED_SEGMENTS: usedSegments, FREE_PCT: freePct, UTILIZED_PCT: utilizedPct, AVG_LEVELS: avgLevels, MAX_LEVELS: maxLevels, MIN_LEVELS: minLevels });
        
        const warehouseGroups: any = {};
        filtered.forEach(item => { const wh = item.WAREHOUSE; if (!warehouseGroups[wh]) warehouseGroups[wh] = { WAREHOUSE: wh, TOTAL_BINS: 0, TOTAL_SEGMENTS: 0, FREE_SEGMENTS: 0, USED_SEGMENTS: 0, LEVELS: [] }; warehouseGroups[wh].TOTAL_BINS++; warehouseGroups[wh].TOTAL_SEGMENTS += item.TOTAL_NO_OF_SEGMENTS || 0; warehouseGroups[wh].FREE_SEGMENTS += item.FREE_SEGMENTS || 0; warehouseGroups[wh].USED_SEGMENTS += (item.TOTAL_NO_OF_SEGMENTS - item.FREE_SEGMENTS) || 0; if (item.NUMBER_OF_LEVELS) warehouseGroups[wh].LEVELS.push(item.NUMBER_OF_LEVELS); });
        setWarehouseData(Object.values(warehouseGroups).map((wh: any) => ({ ...wh, UTILIZATION_PCT: wh.TOTAL_SEGMENTS > 0 ? Math.round((wh.USED_SEGMENTS / wh.TOTAL_SEGMENTS) * 100 * 100) / 100 : 0 })));
        
        const shelfGroups: any = {};
        filtered.forEach(item => { const shelf = item.SHELF_SIZE || 'Unknown'; if (!shelfGroups[shelf]) shelfGroups[shelf] = { SHELF_SIZE: shelf, TOTAL_BINS: 0, TOTAL_SEGMENTS: 0, FREE_SEGMENTS: 0 }; shelfGroups[shelf].TOTAL_BINS++; shelfGroups[shelf].TOTAL_SEGMENTS += item.TOTAL_NO_OF_SEGMENTS || 0; shelfGroups[shelf].FREE_SEGMENTS += item.FREE_SEGMENTS || 0; });
        setShelfData(Object.values(shelfGroups).map((shelf: any) => ({ ...shelf, UTILIZATION_PCT: shelf.TOTAL_SEGMENTS > 0 ? Math.round(((shelf.TOTAL_SEGMENTS - shelf.FREE_SEGMENTS) / shelf.TOTAL_SEGMENTS) * 100 * 100) / 100 : 0 })).sort((a: any, b: any) => b.TOTAL_BINS - a.TOTAL_BINS).slice(0, 10));
        
        const areaGroups: any = {};
        filtered.forEach(item => {
          const area = item.STORAGE_AREA || 'Unknown';
          if (!areaGroups[area]) areaGroups[area] = { STORAGE_AREA: area, TOTAL_BINS: 0, TOTAL_SEGMENTS: 0, FREE_SEGMENTS: 0, LEVELS: [], TEMP_BLOCKED_BINS: 0, TEMP_BLOCKED_PALLETS: 0 };
          areaGroups[area].TOTAL_BINS++;
          areaGroups[area].TOTAL_SEGMENTS += item.TOTAL_NO_OF_SEGMENTS || 0;
          areaGroups[area].FREE_SEGMENTS += item.FREE_SEGMENTS || 0;
          if (item.NUMBER_OF_LEVELS) areaGroups[area].LEVELS.push(item.NUMBER_OF_LEVELS);
          // Count Temp Blocked (Oversized Pallets) - LPFSTA = '2'
          if (String(item.LPFSTA || '').trim() === '2' || item.OCCUPANCY_STATUS === 'TEMP_BLOCKED') {
            areaGroups[area].TEMP_BLOCKED_BINS++;
            areaGroups[area].TEMP_BLOCKED_PALLETS += item.TOTAL_NO_OF_SEGMENTS || 0;
          }
        });
        // Add Area Customer Blocked counts per storage area
        // Filter by selected warehouses first, then extract storage area from STORAGE_LOCATION
        if (areaCustomerBlockedData && areaCustomerBlockedData.length > 0) {
          let filteredAreaBlocked = areaCustomerBlockedData;
          // Filter by selected warehouses
          if (selectedWarehouses.length > 0) {
            filteredAreaBlocked = filteredAreaBlocked.filter((item: any) =>
              selectedWarehouses.includes(item.WAREHOUSE)
            );
          }
          filteredAreaBlocked.forEach((item: any) => {
            // STORAGE_LOCATION is "WAREHOUSE-AREA" format (e.g., "JDA-01"), extract just the area part
            const storageLocation = item.STORAGE_LOCATION || '';
            const area = storageLocation.includes('-') ? storageLocation.split('-')[1] : storageLocation;
            if (areaGroups[area]) {
              if (!areaGroups[area].AREA_BLOCKED_BINS) areaGroups[area].AREA_BLOCKED_BINS = 0;
              if (!areaGroups[area].AREA_BLOCKED_PALLETS) areaGroups[area].AREA_BLOCKED_PALLETS = 0;
              areaGroups[area].AREA_BLOCKED_BINS++;
              areaGroups[area].AREA_BLOCKED_PALLETS += item.TOTAL_NO_OF_SEGMENTS || 1;
            }
          });
        }
        setAreaData(Object.values(areaGroups).map((area: any) => ({ ...area, USED_SEGMENTS: area.TOTAL_SEGMENTS - area.FREE_SEGMENTS, UTILIZATION_PCT: area.TOTAL_SEGMENTS > 0 ? Math.round(((area.TOTAL_SEGMENTS - area.FREE_SEGMENTS) / area.TOTAL_SEGMENTS) * 100 * 100) / 100 : 0, FREE_PCT: area.TOTAL_SEGMENTS > 0 ? Math.round((area.FREE_SEGMENTS / area.TOTAL_SEGMENTS) * 100 * 100) / 100 : 0, AVG_LEVELS: area.LEVELS.length > 0 ? Math.round((area.LEVELS.reduce((a: number, b: number) => a + b, 0) / area.LEVELS.length) * 10) / 10 : 0 })).sort((a: any, b: any) => b.UTILIZATION_PCT - a.UTILIZATION_PCT).slice(0, 20));
        
        // Calculate physical capacity EXCLUDING synthetic manual entries (SOURCE='MANUAL')
        const physicalOnlyData = filtered.filter(item => item.SOURCE !== 'MANUAL');
        const physicalBinCapacity = physicalOnlyData.reduce((sum, item) => sum + (item.TOTAL_NO_OF_SEGMENTS || 0), 0);
        const binUsedPallets = physicalOnlyData.reduce((sum, item) => sum + ((item.TOTAL_NO_OF_SEGMENTS || 0) - (item.FREE_SEGMENTS || 0)), 0);
        const virtualUsedPallets = filteredVirtual.reduce((sum, item) => sum + ((item.TOTAL_NO_OF_SEGMENTS || 0) - (item.FREE_SEGMENTS || 0)), 0);

        let filteredCustomerData = customerStorageData;
        if (selectedWarehouses.length > 0) filteredCustomerData = filteredCustomerData.filter((item: any) => selectedWarehouses.includes(item.WAREHOUSE));
        if (selectedStorageAreas.length > 0) filteredCustomerData = filteredCustomerData.filter((item: any) => item.STORAGE_LOCATION && selectedStorageAreas.some(area => item.STORAGE_LOCATION.includes(area)));

// Detect floor/bulk: matches SQL CASE WHEN COALESCE(P.BIN_COUNT, 0) = 0 THEN 'FLOOR' ELSE 'BIN' END
// Build set of warehouse+LKORT that have physical bins in PLPSKOP (LKASEG != 99)
const binLocSet = new Set<string>();
filtered.forEach(item => {
  if (item.SOURCE === 'MANUAL') return;
  const segs = item.TOTAL_NO_OF_SEGMENTS || 0;
  if (segs === 99) return; // Skip virtual bins (LKASEG=99)
  const wh = (item.WAREHOUSE || '').trim();
  const area = (item.STORAGE_AREA || '').trim();
  // STORAGE_AREA = LKORT-LKBER, extract LKORT (prefix before dash)
  const lkort = area.includes('-') ? area.split('-')[0].trim() : area;
  if (lkort) binLocSet.add(`${wh}__${lkort}`);
});
const floorData = filteredCustomerData.filter((item: any) => {
  if (item.STORAGE_TYPE === 'FLOOR' || item.STORAGE_TYPE === 'BULK') return true;
  if (item.STORAGE_TYPE === 'MANUAL') return false;
  // For IBM/cached data: location with 0 physical bins = FLOOR
  const wh = (item.WAREHOUSE || '').trim();
  const loc = (item.STORAGE_LOCATION || '').trim();
  const key = `${wh}__${loc}`;
  return !binLocSet.has(key);
});
const floorUsedPallets = floorData.reduce((sum: number, item: any) => sum + (item.USED_PALLETS || 0), 0);
// Manual entries come from merged endpoint with STORAGE_TYPE='MANUAL'
const manualOnlyData = filteredCustomerData.filter((item: any) =>
  item.STORAGE_TYPE === 'MANUAL'
);
const manualCustomers = new Set(manualOnlyData.map((item: any) => item.CUSTOMER)).size;
const manualPallets = manualOnlyData.reduce((sum: number, item: any) =>
  sum + (item.USED_PALLETS || 0), 0);
// Capacity: take MAX per warehouse+area, then SUM across areas (not per customer row!)
const manualCapacityByArea: Record<string, number> = {};
manualOnlyData.forEach((item: any) => {
  const key = `${item.WAREHOUSE || ''}__${item.STORAGE_LOCATION || ''}`;
  manualCapacityByArea[key] = Math.max(manualCapacityByArea[key] || 0, item.TOTAL_CAPACITY || 0);
});
const manualCapacity = Object.values(manualCapacityByArea).reduce((sum, cap) => sum + cap, 0);

// ========== TEMP_BLOCKED CALCULATION - FOR ALL WAREHOUSES ==========
// Identify TEMP_BLOCKED bins (LPFSTA = '2' OR OCCUPANCY_STATUS = 'TEMP_BLOCKED')
const tempBlockedData = filtered.filter(item => 
  String(item.LPFSTA || '').trim() === '2' || 
  item.OCCUPANCY_STATUS === 'TEMP_BLOCKED'
);
const tempBlockedBins = tempBlockedData.length;
// For TEMP_BLOCKED: count FULL capacity as blocked (oversized pallets take entire bin)
const tempBlockedPallets = tempBlockedData.reduce((sum, item) => 
  sum + (item.TOTAL_NO_OF_SEGMENTS || 0), 0);

// Recalculate binUsedPallets EXCLUDING temp blocked bins AND manual entries
const normalBinData = filtered.filter(item =>
  String(item.LPFSTA || '').trim() !== '2' &&
  item.OCCUPANCY_STATUS !== 'TEMP_BLOCKED' &&
  item.SOURCE !== 'MANUAL'  // Exclude synthetic manual entries (counted separately)
);
const binUsedPalletsAdjusted = normalBinData.reduce((sum, item) =>
  sum + ((item.TOTAL_NO_OF_SEGMENTS || 0) - (item.FREE_SEGMENTS || 0)), 0);
// ====================================================================

// ========== AREA CUSTOMER BLOCKED CALCULATION - FILTERED BY WAREHOUSE/AREA ==========
// Filter areaCustomerBlockedData based on selected warehouses and storage areas
let filteredAreaBlocked = areaCustomerBlockedData || [];
if (selectedWarehouses.length > 0) {
  filteredAreaBlocked = filteredAreaBlocked.filter((item: any) => selectedWarehouses.includes(item.WAREHOUSE));
}
if (selectedStorageAreas.length > 0) {
  filteredAreaBlocked = filteredAreaBlocked.filter((item: any) =>
    item.STORAGE_LOCATION && selectedStorageAreas.some(area => item.STORAGE_LOCATION.includes(area))
  );
}
const areaBlockedBins = filteredAreaBlocked.length;
const areaBlockedPallets = filteredAreaBlocked.reduce((sum: number, item: any) =>
  sum + (item.TOTAL_NO_OF_SEGMENTS || 1), 0);
const areaFullBlocked = filteredAreaBlocked.filter((item: any) =>
  item.BIN_BLOCK === 'X' && item.PUTAWAY_BLOCK > 0 && item.PICK_BLOCK > 0
).length;
// ==================================================================================

const totalUsedPallets = binUsedPalletsAdjusted + virtualUsedPallets + floorUsedPallets + tempBlockedPallets + manualPallets + areaBlockedPallets;
const physicalBinCapacityWithManual = physicalBinCapacity + manualCapacity;
const totalFreeCapacity = physicalBinCapacityWithManual - totalUsedPallets;
const totalUtilizationPct = physicalBinCapacityWithManual > 0 ? Math.round((totalUsedPallets / physicalBinCapacityWithManual) * 100 * 100) / 100 : 0;
const totalFreePct = physicalBinCapacityWithManual > 0 ? Math.round((totalFreeCapacity / physicalBinCapacityWithManual) * 100 * 100) / 100 : 0;
const uniqueCustomers = new Set(filteredCustomerData.map((item: any) => item.CUSTOMER));

setCustomerStorageStats({
  TOTAL_CUSTOMERS: uniqueCustomers.size,
  PHYSICAL_BIN_CAPACITY: physicalBinCapacity,
 BIN_USED_PALLETS: binUsedPalletsAdjusted,
  VIRTUAL_USED_PALLETS: virtualUsedPallets,
  FLOOR_USED_PALLETS: floorUsedPallets,
  TEMP_BLOCKED_PALLETS: tempBlockedPallets,
  TEMP_BLOCKED_BINS: tempBlockedBins,
  TOTAL_USED_PALLETS: totalUsedPallets,
  BIN_FREE_CAPACITY: totalFreeCapacity > 0 ? totalFreeCapacity : 0,
    TOTAL_CAPACITY_WITH_MANUAL: physicalBinCapacityWithManual,
  BIN_UTILIZATION_PCT: totalUtilizationPct,
  FREE_PCT: totalFreePct > 0 ? totalFreePct : 0,
    MANUAL_CUSTOMERS: manualCustomers,
  MANUAL_PALLETS: manualPallets,
  MANUAL_CAPACITY: manualCapacity,
  AREA_CUSTOMER_BLOCKED_BINS: areaBlockedBins,
  AREA_CUSTOMER_BLOCKED_PALLETS: areaBlockedPallets,
  AREA_CUSTOMER_FULL_BLOCKED: areaFullBlocked
});
        
      } else { setStats(null); setWarehouseData([]); setShelfData([]); setAreaData([]); setCustomerStorageStats(null); }
      
      if (filteredVirtual.length > 0) {
        const vTotalBins = filteredVirtual.length;
        const vTotalSegments = filteredVirtual.reduce((sum, item) => sum + (item.TOTAL_NO_OF_SEGMENTS || 0), 0);
        const vFreeSegments = filteredVirtual.reduce((sum, item) => sum + (item.FREE_SEGMENTS || 0), 0);
        const vUsedSegments = vTotalSegments - vFreeSegments;
        const vFreePct = vTotalSegments > 0 ? Math.round((vFreeSegments / vTotalSegments) * 100 * 100) / 100 : 0;
        const vUtilizedPct = vTotalSegments > 0 ? Math.round((vUsedSegments / vTotalSegments) * 100 * 100) / 100 : 0;
        const vWarehouses = Array.from(new Set(filteredVirtual.map(item => item.WAREHOUSE)));
        setVirtualStats({ TOTAL_BINS: vTotalBins, TOTAL_WAREHOUSES: vWarehouses.length, TOTAL_SHELF_SIZES: 0, TOTAL_STORAGE_AREAS: 0, TOTAL_SEGMENTS: vTotalSegments, FREE_SEGMENTS: vFreeSegments, USED_SEGMENTS: vUsedSegments, FREE_PCT: vFreePct, UTILIZED_PCT: vUtilizedPct, AVG_LEVELS: 0, MAX_LEVELS: 0, MIN_LEVELS: 0 });
        const vWarehouseGroups = filteredVirtual.reduce((acc: any, item) => { const wh = item.WAREHOUSE; if (!acc[wh]) acc[wh] = { WAREHOUSE: wh, TOTAL_BINS: 0, TOTAL_SEGMENTS: 0, FREE_SEGMENTS: 0, USED_SEGMENTS: 0 }; acc[wh].TOTAL_BINS++; acc[wh].TOTAL_SEGMENTS += item.TOTAL_NO_OF_SEGMENTS || 0; acc[wh].FREE_SEGMENTS += item.FREE_SEGMENTS || 0; acc[wh].USED_SEGMENTS += (item.TOTAL_NO_OF_SEGMENTS - item.FREE_SEGMENTS) || 0; return acc; }, {});
        setVirtualWarehouseData(Object.values(vWarehouseGroups).map((wh: any) => ({ ...wh, UTILIZATION_PCT: wh.TOTAL_SEGMENTS > 0 ? Math.round((wh.USED_SEGMENTS / wh.TOTAL_SEGMENTS) * 100 * 100) / 100 : 0 })));
        const vAreaGroups = filteredVirtual.reduce((acc: any, item) => { const area = item.STORAGE_AREA || 'Unknown'; if (!acc[area]) acc[area] = { STORAGE_AREA: area, TOTAL_BINS: 0, TOTAL_SEGMENTS: 0, FREE_SEGMENTS: 0 }; acc[area].TOTAL_BINS++; acc[area].TOTAL_SEGMENTS += item.TOTAL_NO_OF_SEGMENTS || 0; acc[area].FREE_SEGMENTS += item.FREE_SEGMENTS || 0; return acc; }, {});
        setVirtualAreaData(Object.values(vAreaGroups).map((area: any) => ({ ...area, USED_SEGMENTS: area.TOTAL_SEGMENTS - area.FREE_SEGMENTS, UTILIZATION_PCT: area.TOTAL_SEGMENTS > 0 ? Math.round(((area.TOTAL_SEGMENTS - area.FREE_SEGMENTS) / area.TOTAL_SEGMENTS) * 100 * 100) / 100 : 0 })).sort((a: any, b: any) => b.TOTAL_BINS - a.TOTAL_BINS).slice(0, 20));
      } else { setVirtualStats(null); setVirtualWarehouseData([]); setVirtualAreaData([]); }
     } catch (error) { console.error('Error in applyClientSideFilters:', error); } finally { isApplyingFilters.current = false; }
}, [selectedWarehouses, selectedStorageAreas, selectedShelfSizes, selectedLevels, rawWarehouseData, rawVirtualData, customerStorageData, areaCustomerBlockedStats, areaCustomerBlockedData]);

useEffect(() => {
    if (rawWarehouseData.length === 0) return;
    if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
    filterTimeoutRef.current = setTimeout(() => { applyClientSideFilters(); }, 200);
    return () => { if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current); };
// eslint-disable-next-line react-hooks/exhaustive-deps
},  [selectedWarehouses, selectedStorageAreas, selectedShelfSizes, selectedLevels, rawWarehouseData.length, rawVirtualData.length, customerStorageData.length, areaCustomerBlockedStats, areaCustomerBlockedData]);

  useEffect(() => { if (selectedWarehouses.length === 0) { setSelectedStorageAreas([]); setSelectedShelfSizes([]); setSelectedLevels([]); } }, [selectedWarehouses]);
  useEffect(() => { if (activeMenu === 'expire' && expiryStorageData.length === 0 && !expiryLoading) loadExpiryData(); }, [activeMenu, expiryStorageData.length, expiryLoading]);

  const loadAllData = async (forceRefresh: boolean = false) => {
    setLoading(true); setError(''); setLastRefresh(new Date()); setNextRefresh(300);
    try {
      const params: any = {};
      let allDataRes, virtualDataRes, customerStorageRes;

      // ⚡ Use smart cached fetch for all 5 main heavy endpoints (parallel)
      const fetchOpts = { forceRefresh, ttl: 5 * 60 * 1000, timeout: 600000 };
      const [warehouseCache, virtualCache, mergedCache, areaBlockedStatsCache, areaBlockedDataCache, uomCache] = await Promise.all([
        smartFetch(API, '/warehouse', fetchOpts).catch((err: any) => { console.error('Failed to load warehouse data:', err.message); return null; }),
        smartFetch(API, '/warehouse/virtual', fetchOpts).catch((err: any) => { console.error('Failed to load virtual data:', err.message); return null; }),
        smartFetch(API, '/warehouse/customer-storage-merged', fetchOpts).catch((err: any) => { console.error('Failed to load merged customer-storage data:', err.message); return null; }),
        smartFetch(API, '/warehouse/area-customer-blocked/stats', fetchOpts).catch((err: any) => { console.error('Failed to load area-blocked stats:', err.message); return null; }),
        smartFetch(API, '/warehouse/area-customer-blocked', fetchOpts).catch((err: any) => { console.error('Failed to load area-blocked data:', err.message); return null; }),
        smartFetch(API, '/warehouse/uom-breakdown', fetchOpts).catch((err: any) => { console.error('Failed to load UOM data:', err.message); return null; })
      ]);

      allDataRes = warehouseCache ? { data: warehouseCache.data } : null;
      virtualDataRes = virtualCache ? { data: virtualCache.data } : null;

      if (warehouseCache?.fromCache) console.log('⚡ Warehouse data loaded from IndexedDB cache');
      if (virtualCache?.fromCache) console.log('⚡ Virtual data loaded from IndexedDB cache');

      // 🔄 Use MERGED endpoint - combines IBM DB2 + Manual Entries (with cache)
      if (mergedCache) {
        customerStorageRes = { data: mergedCache.data };
        if (mergedCache.fromCache) console.log('⚡ Customer storage loaded from IndexedDB cache');
        if (customerStorageRes.data.success) {
          setCustomerStorageData(customerStorageRes.data.data);
          console.log('📊 Merged customer data:', customerStorageRes.data.stats);
        }
      } else {
        // Fallback to original endpoint if merged fails
        try {
          const fallback = await axios.get(`${API}/warehouse/customer-storage-locations`, { params, timeout: 600000 });
          if (fallback.data.success) setCustomerStorageData(fallback.data.data);
        } catch (e) {
          console.error('Fallback endpoint also failed:', e);
        }
      }

      // Area Customer Blocked data (bins blocked via LGPGM039)
      if (areaBlockedStatsCache?.data.success) {
        setAreaCustomerBlockedStats(areaBlockedStatsCache.data.data);
        console.log('📊 Area Customer Blocked Stats:', areaBlockedStatsCache.data.data);
      }
      if (areaBlockedDataCache?.data.success) {
        setAreaCustomerBlockedData(areaBlockedDataCache.data.data);
        console.log('📊 Area Customer Blocked Data:', areaBlockedDataCache.data.count, 'bins');
      }
      // UOM data — build from frontend customerStorageData (no backend dependency)
      // Try backend first, if available use it; otherwise build from customer storage data
      if (uomCache?.data?.success && uomCache.data.data?.length > 0) {
        setUomData({ summary: uomCache.data.summary || {}, data: uomCache.data.data || [] });
        console.log('📊 UOM Breakdown (from backend):', uomCache.data.data?.length, 'UOM types');
      } else {
        // Build UOM entirely from customer storage data (frontend-only)
        const custData = customerStorageRes?.data?.data || [];
        if (custData.length > 0) {
          const uomMap: Record<string, { UOM: string; PALLET_COUNT: number; TOTAL_QTY: number; RESERVED_QTY: number; clients: Set<string>; areas: Set<string>; warehouses: Set<string> }> = {};
          custData.forEach((item: any) => {
            const uom = ((item.UOM || item.STORAGE_TYPE || 'PLT') + '').trim().toUpperCase();
            const pallets = item.USED_PALLETS || item.PALLET_COUNT || 0;
            const totalQty = item.TOTAL_QTY || item.AVAILABLE_QTY || pallets;
            const reservedQty = item.RESERVED_QTY || 0;
            if (!uomMap[uom]) uomMap[uom] = { UOM: uom, PALLET_COUNT: 0, TOTAL_QTY: 0, RESERVED_QTY: 0, clients: new Set(), areas: new Set(), warehouses: new Set() };
            uomMap[uom].PALLET_COUNT += pallets;
            uomMap[uom].TOTAL_QTY += totalQty;
            uomMap[uom].RESERVED_QTY += reservedQty;
            if (item.CUSTOMER) uomMap[uom].clients.add(item.CUSTOMER.trim());
            if (item.STORAGE_LOCATION || item.STORAGE_AREA) uomMap[uom].areas.add(item.STORAGE_LOCATION || item.STORAGE_AREA);
            if (item.WAREHOUSE) uomMap[uom].warehouses.add(item.WAREHOUSE);
          });
          const derivedUom = Object.values(uomMap).map(u => ({
            UOM: u.UOM, PALLET_COUNT: u.PALLET_COUNT, TOTAL_QTY: Math.round(u.TOTAL_QTY),
            RESERVED_QTY: Math.round(u.RESERVED_QTY), ITEM_COUNT: 0,
            CLIENT_COUNT: u.clients.size, AREA_COUNT: u.areas.size, WAREHOUSE_COUNT: u.warehouses.size
          })).sort((a, b) => b.PALLET_COUNT - a.PALLET_COUNT);
          const totalPallets = derivedUom.reduce((s, d) => s + d.PALLET_COUNT, 0);
          const totalClients = new Set(custData.map((i: any) => (i.CUSTOMER || '').trim()).filter(Boolean)).size;
          setUomData({
            summary: { UOM_COUNT: derivedUom.length, TOTAL_PALLETS: totalPallets, TOTAL_ITEMS: 0, TOTAL_CLIENTS: totalClients },
            data: derivedUom
          });
          console.log('📊 UOM Breakdown (frontend-built from customer data):', derivedUom.length, 'UOM types');
        } else {
          console.log('⚠️ No UOM data available (no customer storage data)');
        }
      }

      // Extract manual entries from the PUBLIC customer-storage-merged endpoint
      // (The /weekly-entries endpoint requires auth which App.tsx doesn't have)
      let manualEntriesData: any[] = [];
      const combinedCustomerData = customerStorageRes?.data?.data ? [...customerStorageRes.data.data] : [];

      // Filter out manual entries from the merged response (STORAGE_TYPE = 'MANUAL')
      manualEntriesData = combinedCustomerData.filter((item: any) =>
        item.STORAGE_TYPE === 'MANUAL'
      );
      setManualEntries(manualEntriesData);
      setCustomerStorageData(combinedCustomerData);

      console.log(`📊 Manual entries from merged data: ${manualEntriesData.length}`);

      if (allDataRes && allDataRes.data.success && allDataRes.data.data && allDataRes.data.data.length > 0) {
        let ibmData = [...allDataRes.data.data];

        // Create synthetic warehouse data from ALL manual entries (not just non-IBM warehouses)
        // manualEntriesData now comes from customer-storage-merged with UPPERCASE field names
        // Group by warehouse + area to get ALL unique areas
        // CAPACITY = take MAX (same area has same capacity, don't sum across clients)
        // PALLETS = SUM closing_balance across all clients in that area
        const manualByWarehouseArea: Record<string, { capacity: number; pallets: number; warehouse: string; area: string; isIbm: boolean }> = {};
        const ibmWarehouses = new Set(ibmData.map((d: any) => d.WAREHOUSE));

        manualEntriesData.forEach((e: any) => {
          const wh = e.WAREHOUSE;  // From merged endpoint (UPPERCASE)
          const area = e.STORAGE_LOCATION || 'MANUAL';
          if (!wh) return;
          const key = `${wh}__${area}`;
          if (!manualByWarehouseArea[key]) {
            manualByWarehouseArea[key] = { capacity: 0, pallets: 0, warehouse: wh, area: area, isIbm: ibmWarehouses.has(wh) };
          }
          // Capacity: take MAX (area capacity is fixed, not summed per client)
          manualByWarehouseArea[key].capacity = Math.max(manualByWarehouseArea[key].capacity, e.TOTAL_CAPACITY || 0);
          // Pallets: SUM closing_balance across all clients in this area
          manualByWarehouseArea[key].pallets += e.USED_PALLETS || 0;
        });

        // Add manual-ONLY warehouse/area combinations to rawWarehouseData (warehouses not in IBM)
        Object.values(manualByWarehouseArea).forEach((data) => {
          if (!data.isIbm) {
            // Add synthetic entry for manual-only warehouse/area
            ibmData.push({
              WAREHOUSE: data.warehouse,
              STORAGE_AREA: data.area,
              SHELF_SIZE: 'MANUAL',
              NUMBER_OF_LEVELS: 1,
              TOTAL_NO_OF_SEGMENTS: data.capacity || 1, // At least 1 to prevent empty
              FREE_SEGMENTS: Math.max(0, (data.capacity || 1) - data.pallets),
              SOURCE: 'MANUAL'
            });
          }
        });

        setRawWarehouseData(ibmData);

        // Add manual-only warehouses to the filter dropdown so users can filter by them
        const manualWarehouseNames = Array.from(new Set(manualEntriesData.map((e: any) => e.WAREHOUSE).filter(Boolean)));
        if (manualWarehouseNames.length > 0) {
          setFilters(prev => {
            const existingWarehouses = prev.warehouses || [];
            const existingWhSet = new Set(existingWarehouses.map(String));
            const newWarehouses = [...existingWarehouses];
            manualWarehouseNames.forEach((wh: any) => {
              if (!existingWhSet.has(String(wh))) newWarehouses.push(wh);
            });
            // Also add manual storage areas to the filter
            const manualAreaNames = Array.from(new Set(manualEntriesData.map((e: any) => e.STORAGE_LOCATION).filter(Boolean)));
            const existingAreas = prev.storageAreas || [];
            const existingAreaSet = new Set(existingAreas.map(String));
            const newAreas = [...existingAreas];
            manualAreaNames.forEach((area: any) => {
              if (!existingAreaSet.has(String(area))) newAreas.push(area);
            });
            return { ...prev, warehouses: newWarehouses, storageAreas: newAreas };
          });
        }
      } else if (manualEntriesData.length > 0) {
        // No IBM data but we have manual entries - create warehouse data from manual only
        // manualEntriesData now comes from customer-storage-merged with UPPERCASE field names
        // Group by warehouse + area to get ALL unique areas
        // CAPACITY = take MAX (same area has same capacity, don't sum across clients)
        // PALLETS = SUM closing_balance across all clients in that area
        const manualByWarehouseArea: Record<string, { capacity: number; pallets: number; warehouse: string; area: string }> = {};
        manualEntriesData.forEach((e: any) => {
          const wh = e.WAREHOUSE;  // From merged endpoint (UPPERCASE)
          const area = e.STORAGE_LOCATION || 'MANUAL';
          if (!wh) return;
          const key = `${wh}__${area}`;
          if (!manualByWarehouseArea[key]) {
            manualByWarehouseArea[key] = { capacity: 0, pallets: 0, warehouse: wh, area: area };
          }
          // Capacity: take MAX (area capacity is fixed, not summed per client)
          manualByWarehouseArea[key].capacity = Math.max(manualByWarehouseArea[key].capacity, e.TOTAL_CAPACITY || 0);
          // Pallets: SUM closing_balance across all clients in this area
          manualByWarehouseArea[key].pallets += e.USED_PALLETS || 0;
        });

        const manualWarehouseData = Object.values(manualByWarehouseArea).map((data) => ({
          WAREHOUSE: data.warehouse,
          STORAGE_AREA: data.area,
          SHELF_SIZE: 'MANUAL',
          NUMBER_OF_LEVELS: 1,
          TOTAL_NO_OF_SEGMENTS: data.capacity || 1,
          FREE_SEGMENTS: Math.max(0, (data.capacity || 1) - data.pallets),
          SOURCE: 'MANUAL'
        }));
        setRawWarehouseData(manualWarehouseData);

        // Add manual warehouses to filter dropdown
        const manualWhs = Array.from(new Set(manualEntriesData.map((e: any) => e.WAREHOUSE).filter(Boolean)));
        const manualArs = Array.from(new Set(manualEntriesData.map((e: any) => e.STORAGE_LOCATION).filter(Boolean)));
        if (manualWhs.length > 0) {
          setFilters(prev => {
            const whSet = new Set((prev.warehouses || []).map(String));
            const areaSet = new Set((prev.storageAreas || []).map(String));
            const newWhs = [...(prev.warehouses || [])];
            const newArs = [...(prev.storageAreas || [])];
            manualWhs.forEach((w: any) => { if (!whSet.has(String(w))) newWhs.push(w); });
            manualArs.forEach((a: any) => { if (!areaSet.has(String(a))) newArs.push(a); });
            return { ...prev, warehouses: newWhs, storageAreas: newArs };
          });
        }
      } else { setError('No data returned from server.'); }
      if (virtualDataRes && virtualDataRes.data.success && virtualDataRes.data.data && virtualDataRes.data.data.length > 0) { setRawVirtualData(virtualDataRes.data.data); }

      setLoading(false);
    } catch (err: any) { setError(err.message); setLoading(false); }
  };

  const loadExpiryData = async () => {
    setExpiryLoading(true);
    try {
      // Use existing /api/expiry endpoint with cache
      const expiryCache = await smartFetch(API, '/expiry', { forceRefresh: false, ttl: 5 * 60 * 1000, timeout: 120000 });
      const { stats, expired, expiring30 } = expiryCache.data;
      if (expiryCache.fromCache) console.log('⚡ Expiry data loaded from IndexedDB cache');

      // Combine expired + expiring30 items and transform field names
      const allItems = [...(expired || []), ...(expiring30 || [])];
      const transformedData = allItems.map((item: any) => ({
        ITEM: item.ITEM_CODE || '',
        ITEM_DESCRIPTION1: item.ITEM_NAME || item.ITEM_DESC || '',
        BATCH_NO: item.BATCH || '',
        COMPANY: item.CUSTOMER || '',
        WAREHOUSE: item.WAREHOUSE || '',
        DAYS_IN_STORAGE: item.DAYS_IN_STORAGE,
        AVAILABLE_QTY: item.QTY || 0,
        RESERVED_QTY: item.RESERVED_QTY || 0,
        EXPIRY_DATE: item.EXPIRY_DATE !== 'No Expiry' ? item.EXPIRY_DATE : null,
        STORAGE_BIN_LOCATION: item.BIN_LOCATION || item.FULL_LOCATION || '',
        EXPIRY_STATUS: item.EXPIRY_STATUS,
        EXPIRY_GROUP: item.EXPIRY_STATUS === 'EXPIRED' ? 'Expired' :
                      item.EXPIRY_STATUS === 'EXPIRING_30_DAYS' ? '0-30 Days' :
                      item.EXPIRY_STATUS === 'EXPIRING_60_DAYS' ? '31-60 Days' :
                      item.EXPIRY_STATUS === 'EXPIRING_90_DAYS' ? '61-90 Days' : 'Other'
      }));
      setExpiryStorageData(transformedData);

      // Transform stats
      const transformedStats = {
        TOTAL_ITEMS: stats?.TOTAL_ITEMS || transformedData.length,
        TOTAL_BATCHES: new Set(transformedData.map((i: any) => i.BATCH_NO).filter(Boolean)).size,
        TOTAL_AVAILABLE_QTY: transformedData.reduce((sum: number, i: any) => sum + (parseFloat(i.AVAILABLE_QTY) || 0), 0),
        EXPIRED_ITEMS: stats?.EXPIRED || transformedData.filter((i: any) => i.EXPIRY_STATUS === 'EXPIRED').length,
        EXPIRING_SOON_90_DAYS: stats?.EXPIRING_90_DAYS || transformedData.filter((i: any) => ['EXPIRING_30_DAYS', 'EXPIRING_60_DAYS', 'EXPIRING_90_DAYS'].includes(i.EXPIRY_STATUS)).length
      };
      setExpiryStats(transformedStats);

      // Calculate storage age distribution from data
      const storageDist: any = { '0-30 days': 0, '31-90 days': 0, '91-180 days': 0, '180+ days': 0 };
      transformedData.forEach((item: any) => {
        const days = item.DAYS_IN_STORAGE;
        if (days !== null && days !== undefined) {
          if (days <= 30) storageDist['0-30 days']++;
          else if (days <= 90) storageDist['31-90 days']++;
          else if (days <= 180) storageDist['91-180 days']++;
          else storageDist['180+ days']++;
        }
      });
      setStorageAgeDistribution(Object.entries(storageDist).map(([STORAGE_AGE, ITEM_COUNT]) => ({ STORAGE_AGE, ITEM_COUNT })));

      // Calculate expiry distribution from data
      const expiryDist: any = { 'Expired': 0, '0-30 Days': 0, '31-90 Days': 0, '91-180 Days': 0, '180+ Days': 0 };
      transformedData.forEach((item: any) => {
        const status = item.EXPIRY_STATUS;
        if (status === 'EXPIRED') expiryDist['Expired']++;
        else if (status === 'EXPIRING_30_DAYS') expiryDist['0-30 Days']++;
        else if (['EXPIRING_60_DAYS', 'EXPIRING_90_DAYS'].includes(status)) expiryDist['31-90 Days']++;
        else expiryDist['91-180 Days']++;
      });
      setExpiryDistribution(Object.entries(expiryDist).map(([EXPIRY_GROUP, ITEM_COUNT]) => ({ EXPIRY_GROUP, ITEM_COUNT })));

      setExpiryLoading(false);
    } catch (error: any) { console.error('Error loading expiry data:', error); setExpiryLoading(false); }
  };

  const formatNumber = (num: number | undefined | null) => new Intl.NumberFormat().format(Math.round(num || 0));
  const formatTime = (seconds: number) => { const mins = Math.floor(seconds / 60); const secs = seconds % 60; return `${mins}:${secs.toString().padStart(2, '0')}`; };
  const resetFilters = () => { setSelectedWarehouses([]); setSelectedStorageAreas([]); setSelectedShelfSizes([]); setSelectedLevels([]); };
  const getCurrentDateTime = () => { const now = new Date(); return { date: now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }), time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) }; };

  const toggleWarehouse = (wh: string) => { setSelectedWarehouses(prev => prev.includes(wh) ? prev.filter(w => w !== wh) : [...prev, wh]); };
  const toggleStorageArea = (area: string) => { setSelectedStorageAreas(prev => prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]); };
  const toggleShelfSize = (size: string) => { setSelectedShelfSizes(prev => prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]); };
  const toggleLevel = (level: string) => { setSelectedLevels(prev => prev.includes(level) ? prev.filter(l => l !== level) : [...prev, level]); };

  if (showHalaRobo) return <HalaRoboAdvanced onBack={() => setShowHalaRobo(false)} />;

  return (
    <div style={{ width: '100vw', minHeight: '100vh', margin: 0, padding: 0, position: 'relative', zIndex: 1 }}>
      <header className="header">
        <div className="header-left">
          <h1>
  <img
    src={`${process.env.PUBLIC_URL}/halapic.jpg`}
    alt="HALA"
    style={{
      width: '40px',
      height: '40px',
      marginRight: '10px',
      verticalAlign: 'middle'
    }}
  />
  HALA Warehouse Intelligence
</h1>
          <p>Executive Command Center • Real-time Analytics & AI Insights</p>
        </div>
        <div className="header-right">
          <ThemeToggle style={{ marginRight: '16px' }} />
          <button className="ai-button" onClick={() => setShowHalaRobo(true)}><img src={`${process.env.PUBLIC_URL}/halarobo-mascot.jpg`} alt="HalaRobo" style={{ width: '24px', height: '24px', borderRadius: '50%', marginRight: '8px', verticalAlign: 'middle' }} /><span>ASK HALAROBO AI</span></button>
          {lastRefresh && (<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}><span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Last Update: {lastRefresh.toLocaleTimeString()}</span>{autoRefreshEnabled && (<div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: 'var(--bg-glass)', borderRadius: 'var(--radius-full)', border: '1px solid var(--border-primary)' }}><span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>🔄 Next: {formatTime(nextRefresh)}</span><button onClick={() => setAutoRefreshEnabled(false)} style={{ padding: '4px 8px', background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)', fontSize: '0.6875rem', fontWeight: 600, cursor: 'pointer' }}>Pause</button></div>)}{!autoRefreshEnabled && (<button onClick={() => setAutoRefreshEnabled(true)} className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '6px 12px' }}>▶ Resume</button>)}</div>)}
          <div className="status">{connected === null ? (<span className="status-loading">⏳ Connecting...</span>) : connected ? (<span className="status-success">LIVE</span>) : (<span className="status-error">OFFLINE</span>)}</div>
        </div>
      </header>
   <nav className="nav-menu">
  <button className={`nav-item ${activeMenu === 'operations' ? 'active' : ''}`} onClick={() => { setActiveMenu('operations'); window.location.hash = ''; }}>
    <span className="nav-icon">📦</span>Operations Console
  </button>
  <button className={`nav-item ${activeMenu === 'customer' ? 'active' : ''}`} onClick={() => { setActiveMenu('customer'); window.location.hash = ''; }}>
    <span className="nav-icon">📈</span>Executive Dashboard
  </button>
  <button className={`nav-item ${activeMenu === 'expire' ? 'active' : ''}`} onClick={() => setActiveMenu('expire')}>
    <span className="nav-icon">⏰</span>Expiry Analytics
  </button>
  <button className={`nav-item ${activeMenu === 'activity' ? 'active' : ''}`} onClick={() => setActiveMenu('activity')}>
    <span className="nav-icon">📊</span>Activity Center
  </button>
  <button className={`nav-item ${activeMenu === 'automail' ? 'active' : ''}`} onClick={() => setActiveMenu('automail')}>
    <span className="nav-icon">📧</span>Auto Reports
  </button>
  <button className={`nav-item ${activeMenu === 'utilization' ? 'active' : ''}`} onClick={() => setActiveMenu('utilization')}>
    <span className="nav-icon">📈</span>Utilization
  </button>
  <button className={`nav-item ${activeMenu === 'capacity' ? 'active' : ''}`} onClick={() => setActiveMenu('capacity')}>
    <span className="nav-icon">🔬</span>Dynamic Capacity Verification
  </button>
  <button className={`nav-item ${activeMenu === 'sales' ? 'active' : ''}`} onClick={() => { setActiveMenu('sales'); window.location.hash = 'sales'; }} style={activeMenu === 'sales' ? { background: 'linear-gradient(135deg, #4668c5, #5b7fd6)', color: '#fff', borderColor: '#4668c5' } : {}}>
    <span className="nav-icon">💼</span>Commercial Dashboard
  </button>
</nav>
      {error && (<div className="error-box"><strong>⚠️ Error:</strong> {error}<button onClick={testConnection} className="btn-retry">🔄 Retry Connection</button></div>)}
      {activeMenu === 'operations' && (<div style={{ padding: 'var(--space-xl)' }}><OperationsConsole /></div>)}
      {activeMenu === 'activity' && (<div style={{ padding: 'var(--space-xl)' }}><ActivityInfoSettings customerStorageData={customerStorageData} /></div>)}
      {activeMenu === 'automail' && (<div style={{ padding: 'var(--space-xl)' }}><InventoryAutoMailScheduler customerStorageData={customerStorageData} warehouseData={warehouseData} stats={stats} virtualStats={virtualStats} expiryStats={expiryStats} />
      </div>)}
      {activeMenu === 'utilization' && (
  <div style={{ padding: 'var(--space-xl)' }}>
    <UtilizationDashboard />
  </div>
)}
      {activeMenu === 'capacity' && (
  <div style={{ padding: 'var(--space-xl)' }}>
    <DynamicCapacityVerification rawWarehouseData={rawWarehouseData} rawVirtualData={rawVirtualData} customerStorageData={customerStorageData} manualEntries={manualEntries} areaCustomerBlockedData={areaCustomerBlockedData} />
  </div>
)}
      {activeMenu === 'expire' && (<div style={{ padding: 'var(--space-xl)' }}><ExpiryStoreDashboard data={expiryStorageData} stats={expiryStats} storageAgeDistribution={storageAgeDistribution} expiryDistribution={expiryDistribution} loading={expiryLoading} /></div>)}
      
      {activeMenu === 'sales' && (<SalesDashboard rawWarehouseData={rawWarehouseData} rawVirtualData={rawVirtualData} customerStorageData={customerStorageData} manualEntries={manualEntries} areaCustomerBlockedData={areaCustomerBlockedData} loading={loading} onRefresh={() => loadAllData(true)} />)}
      {activeMenu === 'customer' && (
        <ExecutiveDashboard
          stats={stats}
          customerStorageStats={customerStorageStats}
          customerStorageData={customerStorageData}
          warehouseData={warehouseData}
          shelfData={shelfData}
          areaData={areaData}
          rawWarehouseData={rawWarehouseData}
          virtualStats={virtualStats}
          virtualWarehouseData={virtualWarehouseData}
          virtualAreaData={virtualAreaData}
          manualEntries={manualEntries}
          areaCustomerBlockedData={areaCustomerBlockedData}
          loading={loading}
          selectedWarehouses={selectedWarehouses}
          filters={filters}
          onRefresh={() => loadAllData(true)}
          onResetFilters={resetFilters}
          onOpenAI={() => setShowHalaRobo(true)}
          formatNumber={formatNumber}
          getWarehouseDisplayName={getWarehouseDisplayName}
          uomData={uomData}
          onNavigate={(page: string) => setActiveMenu(page as any)}
        />
      )}
    </div>
  );
}

const AppWithTheme: React.FC = () => {
  const [hash, setHash] = useState(window.location.hash.replace('#', ''));
  useEffect(() => {
    const onHash = () => setHash(window.location.hash.replace('#', ''));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  if (hash === 'ops-v2') {
    return (
      <ThemeProvider defaultTheme="light">
        <OperationsDashboardV2 />
      </ThemeProvider>
    );
  }
  return (
    <ThemeProvider defaultTheme="dark">
      <App />
    </ThemeProvider>
  );
};

export default AppWithTheme;